import { getAuthService } from '@/auth/auth.service'
import { HttpUtil } from '../http'
import { refreshToken, persistAuthHeaders } from '@/auth/authHandler'
import { isAFailedHttpResponse } from '@/utils/common'

import _isFunction from 'lodash/isFunction'

export const HTTP_METHODS = {
  GET: 'Get',
  POST: 'Post',
  PUT: 'Put',
  DELETE: 'Delete'
}

const noop = () => {}

const methodInvalid = (method) => method === null || !Object.values(HTTP_METHODS).includes(method)

const strategiesInvalid = (strategies) => !strategies.every(_isFunction)

const addAuthHeaders = async (request) => {
  const accesstoken = await getAuthService().getToken()
  if (accesstoken) request.Headers.Authorization = `Bearer ${accesstoken}`
  return request
}

const unauthorized = (res) => {
  return /401/.test(res && (res.status || (res.response && res.response.status)))
}

const noContent = (res) => {
  return /204/.test(res.status)
}

const warnAndInvalidate = (message) => {
  console.warn(message)
  return false
}

const validConfiguration = ({
  method,
  url,
  headers,
  queryParams,
  // responseType,
  // body,
  maxRetry,
  retryFallbackStrategy,
  unauthorizedStrategy,
  failedStrategy,
  noContentStrategy,
  successStrategy,
  failedSideEffect,
  successSideEffect
}) => {
  if (methodInvalid(method)) {
    return warnAndInvalidate('Http method not defined or is invalid')
  }
  if (!url) {
    return warnAndInvalidate('No url configured')
  }
  if (typeof headers !== 'object') {
    return warnAndInvalidate('Headers must be an object')
  }
  if (typeof queryParams !== 'object') {
    return warnAndInvalidate('Parameters must be an object')
  }
  if (typeof maxRetry !== 'number') {
    return warnAndInvalidate('Max retries must be a number')
  }
  const strategies = [retryFallbackStrategy, unauthorizedStrategy, failedStrategy, noContentStrategy, successStrategy, failedSideEffect, successSideEffect]
  if (strategiesInvalid(strategies)) {
    return warnAndInvalidate('Strategies must be functions')
  }

  return true
}

const call = async (configuration, retry = 0) => {
  // build request object
  const request = {
    Url: configuration.url,
    Data: configuration.body,
    Headers: configuration.headers,
    Params: configuration.queryParams,
    ResponseType: configuration.responseType
  }
  // handle retries
  if (retry >= configuration.maxRetry) return await configuration.retryFallbackStrategy(request)
  // configure final request with auth headers
  const finalRequest = await addAuthHeaders(request)
  // make request
  const response = await HttpUtil[configuration.method](finalRequest)
  if (unauthorized(response)) { // handle unauthorized
    // try to refresh token
    const successfulRefresh = await refreshToken()
    if (successfulRefresh) {
      return await call(configuration, retry + 1)
    } else {
      // is unauthorized
      return await configuration.unauthorizedStrategy(response)
    }
  } else if (isAFailedHttpResponse(response)) { // handle failed
    // execute failed side effects
    configuration.failedSideEffect(response)
    return await configuration.failedStrategy(response)
  }
  // persist auth from a successful call
  await persistAuthHeaders(response?.headers)
  // execute success side effects
  configuration.successSideEffect(response)
  if (noContent(response)) { // handle success with no content
    return await configuration.noContentStrategy(response)
  }
  return await configuration.successStrategy(response) // handle success
}

export class HttpBuilder {
  constructor () {
    this.method = null
    this.url = null
    this.headers = {}
    this.queryParams = null
    this.responseType = null
    this.body = null
    this.maxRetry = 2

    this.retryFallbackStrategy = noop
    this.unauthorizedStrategy = noop
    this.failedStrategy = () => false
    this.noContentStrategy = () => true
    this.successStrategy = (res) => res.data

    this.failedSideEffect = noop
    this.successSideEffect = noop
  }

  // CONFIGURE METHOD
  asGet () {
    this.method = HTTP_METHODS.GET
    return this
  }

  asPost () {
    this.method = HTTP_METHODS.POST
    return this
  }

  asPut () {
    this.method = HTTP_METHODS.PUT
    return this
  }

  asDelete () {
    this.method = HTTP_METHODS.DELETE
    return this
  }

  // CONFIGURE URL
  withUrl (url) {
    this.url = url
    return this
  }

  // CONFIGURE HEADERS
  withHeaders (headers) {
    this.headers = headers
    return this
  }

  // CONFIGURE PARAMS
  withParams (params) {
    this.queryParams = params
    return this
  }

  // CONFIGURE RESPONSE TYPE
  withResponseType (responseType) {
    this.responseType = responseType
    return this
  }

  // CONFIGURE BODY
  withBody (body) {
    this.body = body
    return this
  }

  // RETRY
  withMaxRetries (retries) {
    this.maxRetry = retries
  }

  // CONFIGURE STRATEGIES
  withRetryFallbackStrategy (strategy) {
    this.retryFallbackStrategy = strategy
    return this
  }

  withUnauthorizedStrategy (strategy) {
    this.unauthorizedStrategy = strategy
    return this
  }

  withFailedStrategy (strategy) {
    this.failedStrategy = strategy
    return this
  }

  withNoContentStrategy (strategy) {
    this.noContentStrategy = strategy
    return this
  }

  withSuccessStrategy (strategy) {
    this.successStrategy = strategy
    return this
  }

  withFailedSideEffect (strategy) {
    this.failedSideEffect = strategy
    return this
  }

  withSuccessSideEffect (strategy) {
    this.successSideEffect = strategy
    return this
  }

  // VALIDATE CONFIGURATION AND PERFORM REQUEST
  async send () {
    const configuration = {
      method: this.method,
      url: this.url,
      headers: this.headers,
      queryParams: this.queryParams,
      responseType: this.responseType,
      body: this.body,
      maxRetry: this.maxRetry,
      retryFallbackStrategy: this.retryFallbackStrategy,
      unauthorizedStrategy: this.unauthorizedStrategy,
      failedStrategy: this.failedStrategy,
      noContentStrategy: this.noContentStrategy,
      successStrategy: this.successStrategy,
      failedSideEffect: this.failedSideEffect,
      successSideEffect: this.successSideEffect
    }
    if (validConfiguration(configuration)) {
      return await call(configuration)
    }
  }
}
